import ImageBlobReduce from "image-blob-reduce";
import FileUtils from "@logic/FileUtils";
import ImageUploader, { sizeInKbFromBytes } from "@logic/ImageUploader";
import { readPhotoCaptureMetadata } from "@logic/PhotoCaptureMetadata";
import { checkIsNetworkErrorFromError } from "@logic/ErrorHelper";

const ResizeMaxDimensionPx = 3200;
const ResizeJpegQuality = 0.8;
const UploadRetryCountDefault = 2;
const TimeBetweenRetriesMs = 2500;

export interface AtlasUploadValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

export function validateAtlasPhotoFile(file: File): AtlasUploadValidationResult {
  const errorMessage = FileUtils.isFileSupportedForUpload(file.name, file.type, file.size, false);
  return { isValid: !errorMessage, errorMessage: errorMessage || null };
}

export async function resizeAtlasPhoto(file: File): Promise<Blob> {
  const reducer = new ImageBlobReduce();
  reducer._create_blob = function (env) {
    return this.pica.toBlob(env.out_canvas, "image/jpeg", ResizeJpegQuality).then(blob => {
      env.out_blob = blob;
      return env;
    });
  };
  const inputBlob = new Blob([file], { type: file.type || "application/octet-stream" });
  const resizedBlob = await reducer.toBlob(inputBlob, { max: ResizeMaxDimensionPx });
  return resizedBlob;
}

export async function uploadAtlasPhotoWithRetry(
  file: File,
  customerId: string,
  retryCount = UploadRetryCountDefault,
  abortSignal?: AbortSignal
): Promise<string> {
  try {
    const capture = await readPhotoCaptureMetadata(file);
    const resizedBlob = await resizeAtlasPhoto(file);
    const uploadedAssetId = await ImageUploader.uploadItineraryImageBlob(
      resizedBlob,
      true,
      file.name,
      "customer-upload",
      abortSignal,
      undefined,
      customerId,
      capture,
      sizeInKbFromBytes(file.size)
    );
    if (!uploadedAssetId) {
      throw new Error(`Upload did not return an asset ID for file '${file.name}'`);
    }
    return uploadedAssetId;
  } catch (error) {
    if (retryCount > 0 && checkIsNetworkErrorFromError(error)) {
      await new Promise(resolve => setTimeout(resolve, TimeBetweenRetriesMs));
      return uploadAtlasPhotoWithRetry(file, customerId, retryCount - 1, abortSignal);
    }
    throw error;
  }
}
